import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { WordPractice } from "../../components/shared/SpellingSegment";

const BUTTON_STYLE = { backgroundColor: "#1F3B2D", color: "#F5EFE0" };
const TARGET = 10;
const DAILY = 2;

const STAGE_INFO = {
  wild: { emoji: "🥚", title: "Wild", blurb: "Not tamed yet" },
  spotted: { emoji: "👀", title: "Spotted", blurb: "1 to 3 in a row" },
  tamed: { emoji: "🐾", title: "Tamed", blurb: "4 to 9 in a row" },
  mastered: { emoji: "🏆", title: "Mastered", blurb: "10 in a row. Yours for good" },
};

export default function ChildWordHoard() {
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [newWords, setNewWords] = useState("");

  const load = useCallback(
    () =>
      api
        .get("/word-bank")
        .then((res) => setData(res.data))
        .catch(() => {
          setData({ summary: { total: 0, counts: {}, streak: 0 }, words: {}, to_tame_today: [], stages: [] });
          toast.error("Could not load your Word Hoard");
        }),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  if (data === null) {
    return <div className="text-stone-500">Loading…</div>;
  }

  const { summary, words, to_tame_today: queue } = data;
  const resting = data.resting_today || 0;

  // Every practice goes See it, Hear it, Spell it, Type it. Only the typed try counts towards the run of 10.
  const start = (word) => setCurrent(word);

  const nextWord = async () => {
    setCurrent(null);
    await load();
  };

  const addWords = async (e) => {
    e.preventDefault();
    const list = newWords.split(/[\n,]+/).map((w) => w.trim()).filter(Boolean);
    if (!list.length) return;
    try {
      const { data: res } = await api.post("/word-bank/words", { words: list, source: "manual" });
      toast.success(`${res.added.length} new wild word${res.added.length === 1 ? "" : "s"} found`);
      setNewWords("");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not add words");
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="child-word-hoard">
      <div>
        <h1 className="font-display text-3xl font-bold" style={{ color: "#1F3B2D" }}>
          The Word Hoard
        </h1>
        <p className="text-sm text-stone-600 mt-1">
          Spell a word right {TARGET} times in a row to master it. One mistake and the run starts again.
          You get {DAILY} tries per word each day.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Object.keys(STAGE_INFO).map((stage) => (
          <div key={stage} className="paper-card p-3 text-center">
            <div className="text-2xl">{STAGE_INFO[stage].emoji}</div>
            <div className="text-2xl font-bold" style={{ color: "#1F3B2D" }}>
              {summary.counts?.[stage] || 0}
            </div>
            <div className="text-xs text-stone-600">{STAGE_INFO[stage].title}</div>
          </div>
        ))}
        <div className="paper-card p-3 text-center">
          <div className="text-2xl">🔥</div>
          <div className="text-2xl font-bold" style={{ color: "#1F3B2D" }}>
            {summary.streak || 0}
          </div>
          <div className="text-xs text-stone-600">Day streak</div>
        </div>
      </div>

      <section className="paper-card p-6" data-testid="tame-panel">
        <h2 className="font-display text-xl font-bold mb-3" style={{ color: "#1F3B2D" }}>
          Words to practise
        </h2>

        {!current && queue.length === 0 && (
          <p className="text-sm text-stone-700">
            {summary.total === 0
              ? "No words yet. Open a lesson or add some below to begin your hoard."
              : resting > 0
                ? `You have used today's tries on ${resting} word${resting === 1 ? "" : "s"}. Come back tomorrow to keep your runs going!`
                : "Every word is mastered. Brilliant! New words will appear with your next lesson."}
          </p>
        )}

        {!current && queue.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {queue.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => start(w)}
                  className="rounded-full border px-4 py-2 text-sm bg-white hover:translate-y-[-2px] transition"
                  style={{ borderColor: "#C77B5B" }}
                >
                  {STAGE_INFO[w.stage].emoji} Tame a {w.stage} word ({w.streak || 0}/{TARGET}, {w.attempts_left} {w.attempts_left === 1 ? "try" : "tries"} left)
                </button>
              ))}
            </div>
            {resting > 0 && (
              <p className="text-xs text-stone-500">
                {resting} other word{resting === 1 ? " is" : "s are"} resting until tomorrow.
              </p>
            )}
          </div>
        )}

        {current && (
          <div className="space-y-3" data-testid="path-panel">
            <p className="text-sm text-stone-700">
              See it, hear it, spell it, then type it from memory. In a row so far: {current.streak || 0} of {TARGET}.
            </p>
            <WordPractice key={current.id} item={{ word: current.word, tip: current.hint || "" }} lessonId={current.lesson_id || null} />
            <button
              type="button"
              onClick={nextWord}
              className="rounded-full border px-6 py-2.5 text-sm font-bold"
              style={{ borderColor: "#1F3B2D", color: "#1F3B2D" }}
              data-testid="back-to-words"
            >
              Back to my words
            </button>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl font-bold mb-3" style={{ color: "#1F3B2D" }}>
          My hoard
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {Object.keys(STAGE_INFO).map((stage) => (
            <div key={stage} className="paper-card p-4">
              <div className="font-bold" style={{ color: "#1F3B2D" }}>
                {STAGE_INFO[stage].emoji} {STAGE_INFO[stage].title}
                <span className="text-xs font-normal text-stone-500 ml-2">
                  {STAGE_INFO[stage].blurb}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(words[stage] || []).length === 0 && (
                  <span className="text-xs text-stone-400">Nothing here yet</span>
                )}
                {(words[stage] || []).map((w) => (
                  <span
                    key={w.id}
                    className="rounded-full border px-3 py-1 text-sm bg-white"
                    style={{ borderColor: "#D4C8A8" }}
                  >
                    {stage === "wild" ? "???" : w.word}
                    {stage !== "mastered" && (
                      <span className="text-xs text-stone-500 ml-1">
                        {w.streak || 0}/{TARGET}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <form onSubmit={addWords} className="paper-card p-4 space-y-2">
        <label className="text-sm font-bold" style={{ color: "#1F3B2D" }} htmlFor="new-words">
          Found a tricky word? Add it
        </label>
        <textarea
          id="new-words"
          value={newWords}
          onChange={(e) => setNewWords(e.target.value)}
          rows={2}
          placeholder="One word per line, or separate with commas"
          className="w-full rounded-xl border px-3 py-2 text-sm"
          style={{ borderColor: "#D4C8A8" }}
        />
        <button type="submit" className="rounded-full px-5 py-2 text-sm font-bold" style={BUTTON_STYLE}>
          Add to hoard
        </button>
      </form>
    </div>
  );
}
